import { Container, useDisclosure } from '@hover/blueprint';
import React from 'react';
import { generatePath, useHistory } from 'react-router';

import { LeadSheet } from 'src/modules/homeownerApp/LeadSheet/LeadSheet';
import { EmbedFooter } from 'src/modules/homeownerApp/components/Embed/components/EmbedFooter';
import { EmbedHeader } from 'src/modules/homeownerApp/components/Embed/components/EmbedHeader';
import { useFeaturedDisplayImages } from 'src/modules/homeownerApp/components/Embed/hooks/useFeaturedDisplayImages';
import { randomizeArray } from 'src/modules/homeownerApp/hooks';
import { PartnerAffiliateOrgIdProvider } from 'src/modules/homeownerApp/hooks/usePartner';
import { HomeownerAppProvider } from 'src/modules/homeownerApp/providers';
import { MY_UPLOAD_STYLE } from 'src/modules/homeownerApp/static_values';
import { isSample } from 'src/modules/homeownerApp/types';
import { SkipSSR } from 'src/shared/components/SSR/SkipSSR';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { useSetOnetrustCookiesManually } from 'src/shared/hooks/useSetOnetrustCookiesManually';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { Route } from 'src/shared/route';

import { EmbedLandingPage } from './EmbedLandingPage';

const EmbedContainerWithProvider = () => {
  useSetOnetrustCookiesManually();

  const history = useHistory();

  const { hasLeadForm, showLandingPage, originalImageId } = useEmbedContext();

  const { featuredDisplayImages, image } = useFeaturedDisplayImages(
    Number(originalImageId),
  );
  const {
    isOpen: isLeadSheetOpen,
    onClose: onLeadSheetClose,
    onToggle: onLeadSheetToggle,
  } = useDisclosure();
  const { affOrgPublicId } = useGlobalStorageContext();

  const redirectQueryParam = affOrgPublicId
    ? `?aff_org_id=${affOrgPublicId}`
    : '';

  const isSampleImage = isSample(image);

  const aiDisplayImageId = isSampleImage
    ? undefined
    : featuredDisplayImages?.[0]?.id;

  if (aiDisplayImageId && !showLandingPage) {
    history.push({
      pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
        aiDisplayImageId,
      }),
    });
  }

  const designImage = randomizeArray(
    featuredDisplayImages?.filter(
      featuredDisplayImage => featuredDisplayImage.style !== MY_UPLOAD_STYLE,
    ) || [],
  )[0];

  if (!showLandingPage) return null;

  return (
    <PartnerAffiliateOrgIdProvider affOrgId={affOrgPublicId}>
      <Container
        background="neutral.900"
        display="flex"
        flexDirection="column"
        height="100vh"
        justifyContent="space-between"
        width="auto"
      >
        <EmbedHeader />
        {featuredDisplayImages && (
          // We are skipping SSR here because in order to render the edit menu, we have to make several, expensive API calls.
          // If we don't skip SSR, we are waiting for the queries to resolve before the page renders. We could also skip SSR
          // on the queries directly, but this feels simpler for now.
          <SkipSSR>
            <EmbedLandingPage
              featuredDisplayImages={featuredDisplayImages}
              onLeadSheetToggle={onLeadSheetToggle}
              originalImage={featuredDisplayImages?.find(
                img => img.createdFrom === 'original',
              )}
              staticDesignedImage={designImage}
            />
          </SkipSSR>
        )}
        {hasLeadForm && (
          <LeadSheet
            isOpen={isLeadSheetOpen}
            onClose={onLeadSheetClose}
            redirectTo={`/designer/projects/new${redirectQueryParam}`}
          />
        )}
        <EmbedFooter
          background="neutral.900"
          isVertical
          onLeadSheetToggle={onLeadSheetToggle}
        />
      </Container>
    </PartnerAffiliateOrgIdProvider>
  );
};

export const EmbedContainer = () => (
  <HomeownerAppProvider>
    <EmbedContainerWithProvider />
  </HomeownerAppProvider>
);
